import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "@/api/auth.ts";
import { Alert, Button, CodeInput, Field, PageTitle } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { useSetAuthUser } from "./session.ts";

export function VerifyPage({ pending, sentTo }: { pending: string; sentTo: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const nav = useNavigate();
  const setUser = useSetAuthUser();
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);

  const verify = useMutation({
    mutationFn: () => authApi.verify({ pendingUserId: pending, code }),
    onSuccess: ({ user }) => {
      setUser(user);
      void nav({ to: "/" });
    },
  });
  const resend = useMutation({
    mutationFn: () => authApi.resend(pending),
    onSuccess: () => {
      setResent(true);
      setCode("");
      verify.reset();
    },
  });

  return (
    <AuthLayout>
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); verify.mutate(); }}>
        <PageTitle title={t("verify.title")} subtitle={t("verify.subtitle", { email: sentTo })} />
        {verify.error && <Alert tone="danger">{toMsg(verify.error)}</Alert>}
        {resend.error && <Alert tone="danger">{toMsg(resend.error)}</Alert>}
        {resent && !resend.error && <Alert tone="success">{t("verify.resent")}</Alert>}
        <Field label={t("verify.code")} hint={t("verify.expiresIn", { minutes: 10 })}>
          <CodeInput autoFocus required value={code} onChange={setCode} />
        </Field>
        <Button type="submit" className="w-full" loading={verify.isPending} disabled={code.length !== 6}>{t("verify.submit")}</Button>
        <Button type="button" variant="ghost" className="w-full" loading={resend.isPending} onClick={() => resend.mutate()}>{t("verify.resend")}</Button>
      </form>
    </AuthLayout>
  );
}
